import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, Calendar, FileText, Send, Plus } from "lucide-react";
import api from "../../lib/api";
import SearchableSelect from "../SearchableSelect";
import FoldBlock from "./FoldBlock";
import Modal from "../Modal";
import BlurTextarea from "../BlurTextarea";

const TYPES = [
  { v: "call", label: "Call", icon: Phone },
  { v: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { v: "sms", label: "SMS", icon: Send },
  { v: "email", label: "Email", icon: Mail },
  { v: "meeting", label: "Meeting", icon: Calendar },
  { v: "note", label: "Note", icon: FileText },
];

const empty = {
  type: "call",
  direction: "out",
  notes: "",
  duration_min: "",
  contact_id: "",
  linked_lead_id: "",
  linked_jobcard_id: "",
  linked_ticket_id: "",
};

/**
 * Calls/activity block — read-only history (calls are hard evidence and cannot be deleted).
 * "Log activity" launches a quick-add modal.
 */
export default function CallsBlock({
  customerId,
  calls,
  contacts,
  leads,
  jobcards,
  tickets,
  onChanged,
  defaultOpen = false,
}) {
  const [logOpen, setLogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setLogOpen(false);
    setForm(empty);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/calls", {
        customer_id: customerId,
        type: form.type,
        direction: form.direction,
        notes: form.notes,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        contact_id: form.contact_id || null,
        linked_lead_id: form.linked_lead_id || null,
        linked_jobcard_id: form.linked_jobcard_id || null,
        linked_ticket_id: form.linked_ticket_id || null,
      });
      reset();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.put(`/calls/${editing.id}`, {
        notes: editing.notes,
        type: editing.type,
        direction: editing.direction,
        duration_min: editing.duration_min ? Number(editing.duration_min) : null,
      });
      setEditing(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const linkLabel = (call) => {
    if (call.linked_lead_id) {
      const l = leads.find((x) => x.id === call.linked_lead_id);
      return l ? `Lead · ${l.title}` : null;
    }
    if (call.linked_jobcard_id) {
      const j = jobcards.find((x) => x.id === call.linked_jobcard_id);
      return j ? `Job · ${j.title}` : null;
    }
    if (call.linked_ticket_id) {
      const t = tickets.find((x) => x.id === call.linked_ticket_id);
      return t ? `Ticket · ${t.subject}` : null;
    }
    return null;
  };

  return (
    <FoldBlock
      icon={Phone}
      iconColor="#1fb6c1"
      title="Activity / Calls"
      count={calls.length}
      defaultOpen={defaultOpen}
      canEdit
      onAdd={() => setLogOpen(true)}
      addLabel="Log activity"
      testId="block-calls"
      emptyHint="No activity logged yet."
    >
      <div className="space-y-2">
        {calls.slice(0, 50).map((c) => {
          const t = TYPES.find((x) => x.v === c.type) || TYPES[0];
          const link = linkLabel(c);
          // If this activity links to a lead/ticket/jobcard, the row navigates
          // to that entity's full page. Only "loose" calls (no link) open the
          // inline editor — and only because we don't yet have /calls/:id.
          const navHref = c.linked_lead_id
            ? `/leads/${c.linked_lead_id}`
            : c.linked_jobcard_id
              ? `/jobcards/${c.linked_jobcard_id}`
              : c.linked_ticket_id
                ? `/tickets/${c.linked_ticket_id}`
                : `/calls/${c.id}`;
          const inner = (
            <>
              <div
                className={`mt-0.5 w-7 h-7 rounded-md flex items-center justify-center border shrink-0 ${
                  c.direction === "in"
                    ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300"
                    : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                }`}
              >
                <t.icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider gap-2">
                  <span className="text-[#a0aabf]">
                    {t.label} · {c.direction === "in" ? "In" : "Out"}
                    {c.duration_min ? ` · ${c.duration_min}m` : ""}
                  </span>
                  <span className="text-[#647087] shrink-0">
                    {new Date(c.created_at).toLocaleString('en-GB')}
                  </span>
                </div>
                {c.notes && (
                  <div className="text-xs mt-1 text-[#dde3ec] whitespace-pre-wrap line-clamp-2">
                    {c.notes}
                  </div>
                )}
                <div className="text-[10px] text-[#647087] mt-1">
                  by <span className="text-[#1fb6c1]">{c.by_user_name}</span>
                  {link && <> · <span className="text-[#1FB6C1]">{link}</span></>}
                </div>
              </div>
            </>
          );
          const cls = "w-full flex gap-3 text-left p-2 rounded-md border border-white/5 hover:border-[#1fb6c1]/30 bg-white/[0.02] transition-colors";
          return (
            <div key={c.id} className="flex items-stretch gap-1" data-testid={`call-${c.id}`}>
              <Link to={navHref} className={cls + " flex-1"} data-testid={`call-${c.id}-nav`}>{inner}</Link>
              <button
                type="button"
                onClick={() => setEditing({ ...c })}
                className="hv-btn hv-btn-ghost px-2 py-1 text-[10px]"
                title="Edit"
                data-testid={`call-${c.id}-edit`}
              >
                Edit
              </button>
            </div>
          );
        })}
        {calls.length > 50 && (
          <div className="text-[10px] text-[#647087] text-center pt-1">
            Showing the latest 50 of {calls.length}.
          </div>
        )}
      </div>

      {/* Log activity modal */}
      <Modal open={logOpen} onClose={reset} title="Log activity" testId="call-add-modal">
        <form onSubmit={submit} className="space-y-3" data-testid="call-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="hv-input mt-1"
                data-testid="call-input-type"
              >
                {TYPES.map((t) => (
                  <option key={t.v} value={t.v}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Direction</label>
              <select
                value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })}
                className="hv-input mt-1"
              >
                <option value="out">Outbound</option>
                <option value="in">Inbound</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Contact</label>
            <select
              value={form.contact_id}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              className="hv-input mt-1"
            >
              <option value="">— Customer —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.role ? `(${c.role})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
            <BlurTextarea
              value={form.notes}
              onSave={(v) => setForm((f) => ({ ...f, notes: v }))}
              testid="call-input-notes"
              placeholder="What was discussed?"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Duration (min)</label>
              <input
                type="number"
                value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                className="hv-input mt-1"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Lead</label>
              <SearchableSelect
                value={form.linked_lead_id || ""}
                onChange={(id) =>
                  setForm({
                    ...form,
                    linked_lead_id: id,
                    linked_jobcard_id: "",
                    linked_ticket_id: "",
                  })
                }
                options={leads.map((l) => ({
                  id: l.id,
                  label: l.title || "Untitled lead",
                  sublabel: [l.customer_name, l.phase].filter(Boolean).join(" · "),
                  searchTerms: [l.title, l.customer_name, l.phase, l.industry, l.contact_name].filter(Boolean),
                }))}
                placeholder="—"
                testId="callsblock-input-linked-lead"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Job</label>
              <select
                value={form.linked_jobcard_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    linked_jobcard_id: e.target.value,
                    linked_lead_id: "",
                    linked_ticket_id: "",
                  })
                }
                className="hv-input mt-1"
              >
                <option value="">—</option>
                {jobcards.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.short_id ? `#${j.short_id} ` : ""}
                    {j.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            className="hv-btn hv-btn-primary w-full"
            data-testid="call-submit"
          >
            <Plus className="w-4 h-4" /> {busy ? "Saving…" : "Log activity"}
          </button>
        </form>
      </Modal>

      {/* Edit call modal (notes only — calls cannot be deleted) */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit activity"
        testId="call-edit-modal"
      >
        {editing && (
          <div className="space-y-3" data-testid="call-edit-form">
            <div className="text-[10px] text-[#647087]">
              by <span className="text-[#1fb6c1]">{editing.by_user_name}</span> ·{" "}
              {new Date(editing.created_at).toLocaleString('en-GB')}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Type</label>
                <select
                  value={editing.type || "call"}
                  onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                  className="hv-input mt-1"
                >
                  {TYPES.map((t) => (
                    <option key={t.v} value={t.v}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Duration (min)</label>
                <input
                  type="number"
                  value={editing.duration_min ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, duration_min: e.target.value })
                  }
                  className="hv-input mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
              <BlurTextarea
                value={editing.notes}
                onSave={(v) => setEditing((s) => ({ ...s, notes: v }))}
                testid="call-edit-input-notes"
              />
            </div>
            <div className="text-[10px] text-[#647087] italic">
              🔒 Calls cannot be deleted — they are hard evidence. Edit notes only.
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <button type="button" onClick={() => setEditing(null)} className="hv-btn hv-btn-ghost">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={busy}
                className="hv-btn hv-btn-primary"
                data-testid="call-edit-save"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </FoldBlock>
  );
}
